// Every screen once, as data: its canonical title and primary action (content.json F1 and the flow briefs), the record
// cards it shows, and its named controls. The Screens concept renders a page from it; the Chat concept renders the same
// cards beside a labeled assistant explanation and a composer. Both read and change the one ledger.
import { useEffect, type ReactNode } from 'react';
import { useStore, type Page } from './store.tsx';
import { Button, Chevron, Row } from './ds.tsx';
import { CaseCard, DealsCard, DealsNow, HomeCard, MembershipCard, OfferPrices, OpenItems, OrdersList, PaymentForm, PhotoButtons, PhotoList, PreparingCard,
  QuoteCard, ReceiptCard, RecordsCards, RenewalCards, ReviewCard, InfoChoices, RoutineHelp, T, UnitsCard, VisitDetail } from './cards.tsx';
import { W } from './words.ts';
import { F1, control, view } from '../../content/content.ts';

export interface Action { label: string; onClick: () => void; disabled?: boolean }
export interface ScreenDef {
  eyebrow: string; title: string;
  /** The canonical lead paragraph a Screens page shows under its title (F1 copy). */
  lead?: string;
  /** What the assistant says beside the records in the Chat concept. */
  intro: string;
  body: ReactNode;
  primary?: Action;
  /** The view's named controls (content.json), as quiet actions. */
  controls: Action[];
  /** Pages inside the club home get a way back to it. */
  home?: boolean;
}

/** A research control, not a screen element: the facilitator's stand-in for BOB issuing the quote. */
function ResearchControl() {
  const { issue, busy, heldWait } = useStore();
  if (!heldWait) return null;
  return <button type="button" className="rs-control rs-press-soft" onClick={issue} disabled={!!busy}><span>{W.control}</span><Chevron /></button>;
}

export function useScreen(pick: () => void): ScreenDef {
  const s = useStore();
  const h = s.h;
  const p = s.records?.purchase ?? null;
  const page = s.page;
  // A member's visit request opens a visit-only quote: the annual fee is never added again.
  useEffect(() => {
    if (page === 'request' && h?.membership.state === 'active' && p?.quote.kind !== 'visit') s.act({ type: 'request_visit' });
  }, [page, h?.membership.state]);

  if (page) return pageDef(page, s, pick);
  const f = F1[s.view - 1];
  const v = view(1, s.view);
  const c = (i: number) => control(1, s.view, i);
  switch (s.view) {
    case 1: return {
      eyebrow: v.title, title: f.title, intro: f.copy,
      lead: f.copy, body: <><OfferPrices /><RoutineHelp /><DealsNow />
        <section className="hc-card rs-rows"><Row label={W.notDecided} value={<span className="hc-bracket">[unresolved]</span>} onClick={() => s.setSheet('unresolved')} /></section></>,
      primary: { label: f.action, onClick: s.begin },
      controls: [{ label: c(0), onClick: () => s.setSheet('membership') }, { label: c(1), onClick: () => s.setSheet('visits') }, { label: c(3), onClick: () => s.openExc('decline') }],
    };
    case 2: return {
      eyebrow: v.title, title: f.title, intro: f.copy,
      lead: f.copy, body: <><HomeCard /><UnitsCard /><PhotoButtons pick={pick} /><PhotoList /></>,
      primary: { label: f.action, onClick: s.prepare, disabled: !!s.busy },
      controls: [{ label: c(2), onClick: () => s.contact() }, { label: W.notNow, onClick: s.notNow }],
    };
    case 3: return {
      eyebrow: v.title, title: f.title, intro: f.copy,
      lead: f.copy, body: <><PreparingCard /><ResearchControl /></>,
      primary: { label: f.action, onClick: () => s.openRoute(W.returnLater, W.returnNote) },
      controls: [...(s.records?.basis.status === 'scoped_review' ? [{ label: c(1), onClick: () => s.contact('scope') }] : []), { label: control(1, 2, 1), onClick: () => s.setView(2) }],
    };
    case 4: return {
      eyebrow: v.title, title: f.title, intro: 'Membership and a visit are separate choices. A visit is not included or ordered by joining.',
      body: <QuoteCard />,
      primary: { label: f.action, onClick: () => { s.acceptVersion(); s.setView(5); }, disabled: !p || p.total === null },
      controls: [{ label: c(0), onClick: () => s.setSheet('membership') }, { label: control(1, 2, 1), onClick: () => s.setView(2) }, { label: W.notNow, onClick: s.notNow }],
    };
    case 5: return {
      eyebrow: v.title, title: f.title, intro: f.copy,
      lead: f.copy, body: <><ReviewCard /><InfoChoices /><PaymentForm total={p?.total ?? null} onPay={s.pay} /></>,
      controls: [{ label: c(1), onClick: () => s.setView(4) }, { label: c(2), onClick: () => s.openExc('decline') }],
    };
    case 6: {
      const a = h?.payments.filter((x) => x.for === 'join').at(-1);
      return {
        eyebrow: v.title, title: f.title, intro: f.copy, lead: f.copy,
        body: <>{a ? <ReceiptCard payment={a} /> : <p className="hc-caption">No payment attempt yet.</p>}</>,
        primary: { label: f.action, onClick: s.seeStatus },
        controls: [{ label: c(1), onClick: () => s.contact() }],
      };
    }
    case 7: return {
      eyebrow: v.title, title: f.title, intro: f.copy, lead: f.copy,
      body: <><MembershipCard /><OrdersList /></>,
      primary: { label: f.action, onClick: () => s.setView(8) },
      controls: [{ label: c(2), onClick: () => s.openPage('request') }],
    };
    default: return {
      eyebrow: v.title, title: f.title, intro: f.copy, lead: f.copy,
      body: <><MembershipCard /><OrdersList /><OpenItems /><DealsNow />
        <section className="hc-card rs-rows">
          <Row label={W.dealsAndBenefits} value={null} onClick={() => s.openPage('deals')} />
          <Row label={W.recordsAndChoices} value={null} onClick={() => s.openPage('records')} />
          <Row label={W.renewLeave} value={null} onClick={() => s.openPage('membership')} />
        </section>
        {(h?.cases ?? []).length > 0 && <section className="hc-card rs-rows">{h!.cases.map((k) => <Row key={k.id} label={`${W.case} ${k.id}`} value={null} onClick={() => s.openPage(`case:${k.id}`)} />)}</section>}</>,
      primary: h?.membership.state === 'active' ? { label: f.action, onClick: () => s.openPage('request') } : undefined,
      controls: [{ label: control(1, 8, 0), onClick: () => s.openPage('deals') }, { label: control(1, 8, 3), onClick: () => s.contact() }],
    };
  }
}

function pageDef(page: NonNullable<Page>, s: ReturnType<typeof useStore>, pick: () => void): ScreenDef {
  const p = s.records?.purchase ?? null;
  if (page.startsWith('visit:')) {
    const id = page.slice(6);
    return { eyebrow: view(2, 1).title, title: `${W.order} ${id}`, intro: view(2, 1).assistant ?? '', body: <VisitDetail id={id} />, controls: [], home: true };
  }
  if (page.startsWith('case:')) {
    const k = s.h?.cases.find((x) => x.id === page.slice(5));
    return { eyebrow: view(4, 3).title, title: `${W.case} ${page.slice(5)}`, intro: view(4, 3).assistant ?? '', body: k ? <CaseCard k={k} /> : <p className="hc-caption">No such case.</p>,
             controls: [{ label: control(4, 3, 1), onClick: () => s.contact(k?.about ?? null) }], home: true };
  }
  switch (page) {
    case 'request': return {
      eyebrow: view(2, 1).title, title: control(2, 1, 0), intro: view(2, 1).assistant ?? '',
      body: <><UnitsCard /><PhotoButtons pick={pick} /><PhotoList />{p?.quote.kind === 'visit' && <><QuoteCard /><PaymentForm total={p.total} onPay={s.pay} label={p.total !== null ? `Simulate purchase of this visit · $${p.total}` : undefined} /></>}</>,
      controls: [...(s.records?.basis.status === 'scoped_review' ? [{ label: control(1, 3, 1), onClick: () => s.contact('scope') }] : [])], home: true,
    };
    case 'deals': return { eyebrow: view(5, 1).title, title: W.dealsAndBenefits, intro: view(5, 1).assistant ?? '', body: <DealsCard />, controls: [], home: true };
    case 'records': return { eyebrow: view(6, 1).title, title: W.recordsAndChoices, intro: view(6, 1).assistant ?? '', body: <RecordsCards />, controls: [], home: true };
    default: return { eyebrow: view(7, 1).title, title: W.renewLeave, intro: view(7, 5).assistant ?? '', body: <RenewalCards />, controls: [], home: true };
  }
}

export { T };
